/**
 * Error classification types
 */
export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR', // User's internet is down or backend unreachable
  AUTH_SERVICE_DOWN = 'AUTH_SERVICE_DOWN', // Auth service error (503)
  INVALID_SESSION = 'INVALID_SESSION', // Invalid/expired token (should logout)
  SERVER_ERROR = 'SERVER_ERROR', // Server-side error (500)
  UNKNOWN = 'UNKNOWN', // Unclassified error
}

export interface ClassifiedError extends Error {
  type: ErrorType;
  status?: number;
  code?: string;
  shouldRetry: boolean;
  shouldLogout: boolean;
}

/**
 * Check if error is a network error (fetch failed, no response)
 */
const isNetworkError = (error: unknown): boolean => {
  // TypeError is thrown by fetch when network is down
  if (error instanceof TypeError) {
    const msg = error.message.toLowerCase();
    return msg.includes('fetch') || msg.includes('network') || msg.includes('failed to fetch');
  }
  return false;
};

/**
 * Classify error into categories for appropriate handling
 */
export const classifyError = (error: unknown): ClassifiedError => {
  // Network errors (no response from server)
  if (isNetworkError(error)) {
    return Object.assign(new Error('Network connection failed'), {
      type: ErrorType.NETWORK_ERROR,
      shouldRetry: true,
      shouldLogout: false,
    });
  }

  // Extract error details (cast to any for property access since error shape is unknown)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;
  const status = err?.status || err?.response?.status;
  const code = err?.code || err?.response?.data?.code;

  // Auth service down (503 with AUTH_SERVICE_UNAVAILABLE)
  if (status === 503 && code === 'AUTH_SERVICE_UNAVAILABLE') {
    return Object.assign(new Error('Authentication service temporarily unavailable'), {
      type: ErrorType.AUTH_SERVICE_DOWN,
      status,
      code,
      shouldRetry: true,
      shouldLogout: false,
    });
  }

  // Invalid session (401 with specific codes)
  if (status === 401 && (code === 'INVALID_JWT' || code === 'NO_TOKEN')) {
    return Object.assign(new Error('Session expired'), {
      type: ErrorType.INVALID_SESSION,
      status,
      code,
      shouldRetry: false,
      shouldLogout: true,
    });
  }

  // Server error (500)
  if (status === 500) {
    return Object.assign(new Error('Server error'), {
      type: ErrorType.SERVER_ERROR,
      status,
      code,
      shouldRetry: false,
      shouldLogout: false,
    });
  }

  // Unknown error
  return Object.assign(new Error(err?.message || 'An error occurred'), {
    type: ErrorType.UNKNOWN,
    status,
    code,
    shouldRetry: false,
    shouldLogout: false,
  });
};

/**
 * Check if error is a network error (convenience function)
 */
export const isNetworkFailure = (error: unknown): boolean => {
  const classified = classifyError(error);
  return classified.type === ErrorType.NETWORK_ERROR || classified.type === ErrorType.AUTH_SERVICE_DOWN;
};

/**
 * Check if error requires logout (convenience function)
 */
export const requiresLogout = (error: unknown): boolean => {
  const classified = classifyError(error);
  return classified.shouldLogout;
};

/**
 * Format an API error for display to users.
 * Extracts validation details if present, otherwise returns the main error message.
 *
 * API validation errors have this shape:
 * {
 *   error: "Validation failed",
 *   details: [{ path: "fieldName", message: "Error message" }, ...]
 * }
 */
export const formatApiError = (error: unknown): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;

  // Check for validation details array
  if (err?.details && Array.isArray(err.details) && err.details.length > 0) {
    const detailMessages = err.details
      .map((d: { path?: string; message?: string }) => d.message || 'Unknown error')
      .filter((msg: string) => msg)
      .join('. ');

    if (detailMessages) {
      return detailMessages;
    }
  }

  // Fall back to main error message
  return err?.message || 'An error occurred';
};
